import { gql, useLazyQuery, useQuery } from '@apollo/client'
import styled from 'styled-components'

import PriceBreakdown from 'productTemplate/components/ProductPicker/PriceBreakdown'
import { useProductTemplateContext } from 'productTemplate/components/ProductTemplateContextProvider'
import Spinner from 'shared/components/Spinner'
import ProductQA from 'shared/dataAttributes/ProductQA'
import { colors, fonts } from 'shared/lib'
import Badge from '../../Badge'
import ModalPanel from '../../ModalPanel'
import { t } from 'localization'
import { useEffect } from 'react'
import { useShoppingRegionContext } from 'shared/contexts/ShoppingRegionContextProvider'
import { useGetBuyBarCopyQuery } from 'api/productVariantsApi'
import ReactMarkdown from 'react-markdown'
import { useFeatureFlag } from 'featureFlags/hooks/useFeatureFlag'
import { FeatureFlag } from 'shared/enums/FeatureFlag'

const TP = 'productTemplate.components.ProductPicker.panels.FastToYouPanel'

const FastestToYouText = styled.div`
  margin-top: 15px;
  ${fonts.BODY_TEXT};
  color: ${colors.FC2_BLACK};
`

export const GET_PRICE_BREAKDOWN = gql`
  query getPriceBreakdown(
    $id: ID!
    $countryCode: String
    $boxCondition: String
    $shoeCondition: String
    $size: String
    $priceCents: Int!
    $isInstantShip: Boolean
    $productId: String
  ) {
    getPriceBreakdown(
      id: $id
      countryCode: $countryCode
      boxCondition: $boxCondition
      shoeCondition: $shoeCondition
      size: $size
      priceCents: $priceCents
      isInstantShip: $isInstantShip
      productId: $productId
    ) {
      priorityFeePercent
      markupFee {
        display
        value
      }
      basePrice {
        display
        value
      }
      totalPrice {
        display
        value
      }
    }
  }
`

const FastestToYouPanel = () => {
  const hideFastestToYouPricing = useFeatureFlag(FeatureFlag.TEMP_WEB_FC_PDP_HIDE_FASTEST_TO_YOU)
  const { productTemplate, selectedSize, orderInfo } = useProductTemplateContext()
  const {
    currencyCode: selectedCurrency,
    country: selectedCountryCode,
  } = useShoppingRegionContext()
  const priceCents = selectedSize?.new?.instantShipLowestPriceCents?.value || 0
  const boxCondition = selectedSize?.new?.boxCondition || ''
  const shoeCondition = selectedSize?.new?.shoeCondition || ''
  const size = selectedSize?.size.value || ''
  const [getPriceBreakdown, { loading, data, refetch: fetchPriceBreakdownData }] = useLazyQuery(GET_PRICE_BREAKDOWN, {
    variables: {
      id: productTemplate.id,
      countryCode: selectedCountryCode,
      boxCondition,
      shoeCondition,
      size: `${size}`,
      priceCents,
      isInstantShip: true, // always send true in case lowest price shoe is selected
      productId: orderInfo?.productId,
    },
  })

  const { slug } = productTemplate
  const { data: pricingModalCopy } = useGetBuyBarCopyQuery({ slug, country: selectedCountryCode })
  const fallbackCopy =
    'Product is pre-verified and ready to ship from a Flight Club facility. Priority Processing includes up to an 8% fee to ensure your order is prioritized for quicker delivery to you.'


  //Only get make the pricing breakdown call on mount if FF is off
  useEffect(() => {
    if (!hideFastestToYouPricing) {
      getPriceBreakdown()
    }
  }, [])
  // refetch price breakdown when currency changes
  useEffect(() => {
    if (!hideFastestToYouPricing) {
      fetchPriceBreakdownData()
    }
  }, [selectedCurrency])

  return (
    <ModalPanel hasDivider={false} qaAttr={ProductQA.PopupFastestToYou}>
      <Badge
        qaAttr={ProductQA.PopupFastestToYouBadge}
        text={t(`${TP}.popupFastestToYouBadge`, 'Fastest to you')}
        badgeType="primary2"
        data-testid="fastestToYouBadge"
      />
      <FastestToYouText data-qa={ProductQA.PopupFastestToYouText} data-testid="fastestToYouText">
        <ReactMarkdown>
          {pricingModalCopy?.pricingModalFastestToYouCopy || fallbackCopy}
        </ReactMarkdown>
      </FastestToYouText>
      {loading && <Spinner showSpinner />}
      {data?.getPriceBreakdown && !hideFastestToYouPricing && (
        <PriceBreakdown
          basePrice={data?.getPriceBreakdown?.basePrice}
          data-testid="pricingBreakdown"
          markupFee={data?.getPriceBreakdown?.markupFee}
          totalPrice={data?.getPriceBreakdown?.totalPrice}
        />
      )}
    </ModalPanel>
  )
}

export default FastestToYouPanel
